import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, CalendarClock, Phone, Mail, Wrench, AlertTriangle, Loader2 } from "lucide-react";
import { API, beDate } from "@/lib/studioApi";

const STATE = {
  en_retard: ["Entretien en retard", "bg-rose-100 text-rose-800"],
  a_programmer: ["Entretien à prévoir", "bg-amber-100 text-amber-900"],
  a_jour: ["Installation à jour", "bg-emerald-100 text-emerald-800"],
  inconnu: ["Date d'entretien inconnue", "bg-slate-100 text-[#5C6A87]"],
};

export default function PublicPassport() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/pt/${code}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError("Ce passeport technique est introuvable."));
  }, [code]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0F2645] px-6">
        <div data-testid="passport-error" className="max-w-sm text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-400" />
          <p className="mt-3 font-heading text-lg font-bold text-white">{error}</p>
          <p className="mt-2 text-sm text-[#93A0BC]">Vérifiez le QR code ou contactez votre installateur.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F3F5FC]">
        <Loader2 className="h-6 w-6 animate-spin text-[#3A5CC8]" />
      </div>
    );
  }

  const { equipment: eq, site, installer, log } = data;
  const [stateLabel, stateCls] = STATE[eq.maintenance_state] || STATE.inconnu;

  return (
    <div className="min-h-screen bg-[#F3F5FC] pb-12">
      <header className="px-5 pb-10 pt-8 text-white" style={{ backgroundColor: "#0F2645" }}>
        <div className="mx-auto max-w-lg">
          {installer.logo_media_id ? (
            <span className="mb-4 inline-flex rounded-xl bg-white px-3 py-2">
              <img src={`${API}/media/${installer.logo_media_id}`} alt={installer.company} className="h-8 w-auto" />
            </span>
          ) : (
            <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.2em] text-[#93A0BC]">Passeport technique</p>
          )}
          <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: installer.color }}>
            {eq.category_label}
          </p>
          <h1 data-testid="passport-name" className="mt-1 font-heading text-2xl font-extrabold leading-tight">{eq.name}</h1>
          <p className="mt-2 text-sm text-slate-300">
            {[site.name, site.city].filter(Boolean).join(" · ")}
          </p>
        </div>
      </header>

      <main className="mx-auto -mt-6 max-w-lg space-y-3 px-4">
        <div data-testid="passport-state" className={`rounded-2xl px-4 py-3.5 text-sm font-bold shadow-sm ${stateCls}`}>
          <span className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" /> {stateLabel}
          </span>
          {eq.next_maintenance && (
            <span className="mt-1 block text-xs font-medium opacity-80">
              Prochain entretien conseillé : {beDate(eq.next_maintenance)}
            </span>
          )}
        </div>

        <div className="rounded-2xl border border-[#E2E7F7] bg-white p-4">
          <h2 className="font-heading text-sm font-bold text-[#0F2645]">L'appareil</h2>
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
            {[
              ["Marque", eq.brand],
              ["Modèle", eq.model],
              ["N° de série", eq.serial],
              ["Puissance", eq.power_kw ? `${eq.power_kw} kW` : ""],
              ["Emplacement", eq.location],
              ["Installé le", eq.install_date ? beDate(eq.install_date) : ""],
              ["Dernier entretien", eq.last_maintenance ? beDate(eq.last_maintenance) : ""],
              ["Type de bâtiment", site.building_type_label],
              ["Classe PEB", site.peb_class ? `PEB ${site.peb_class}` : ""],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="min-w-0">
                <dt className="text-[10px] font-bold uppercase tracking-wider text-[#93A0BC]">{k}</dt>
                <dd className="truncate text-sm font-semibold text-[#0F2645]">{v}</dd>
              </div>
            ))}
          </dl>
          {eq.under_warranty && (
            <p data-testid="passport-warranty" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
              <ShieldCheck className="h-3.5 w-3.5" /> Sous garantie jusqu'au {beDate(eq.warranty_until)}
            </p>
          )}
        </div>

        <div className="rounded-2xl border border-[#E2E7F7] bg-white p-4">
          <h2 className="font-heading text-sm font-bold text-[#0F2645]">Historique des interventions</h2>
          {log.length === 0 ? (
            <p className="mt-2 text-xs text-[#5C6A87]">Aucune intervention enregistrée pour le moment.</p>
          ) : (
            <div className="mt-3 space-y-2" data-testid="passport-log">
              {log.map((l, i) => (
                <div key={i} className="flex items-start gap-3 border-t border-[#E2E7F7] pt-2 first:border-0 first:pt-0">
                  <span className="w-[70px] shrink-0 text-xs font-bold tabular-nums text-[#5C6A87]">{beDate(l.date)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[#0F2645]">{l.type_label}</span>
                    {l.notes && <span className="block text-xs leading-relaxed text-[#5C6A87]">{l.notes}</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl p-4 text-white" style={{ backgroundColor: installer.color }}>
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">Votre installateur</p>
          <p data-testid="passport-installer" className="mt-1 font-heading text-lg font-extrabold">{installer.company}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {installer.phone && (
              <a href={`tel:${installer.phone.replace(/\s/g, "")}`} data-testid="passport-call" className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-white/15 px-4 text-sm font-bold backdrop-blur">
                <Phone className="h-4 w-4" /> {installer.phone}
              </a>
            )}
            {installer.email && (
              <a href={`mailto:${installer.email}`} className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-white/15 px-4 text-sm font-bold backdrop-blur">
                <Mail className="h-4 w-4" /> Écrire
              </a>
            )}
          </div>
        </div>

        <p className="flex items-center justify-center gap-1.5 pt-2 text-[11px] text-[#93A0BC]">
          <Wrench className="h-3 w-3" /> Passeport technique généré par Devisto
        </p>
      </main>
    </div>
  );
}
